import { Stack, useTheme } from "@mui/material";
import { Banknote, Box } from "lucide-react";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import {
	ClientCarBodyTypeEnum,
	ClientCarFuelEnum,
	ClientPropertyCarVehicleTypeEnum,
	type ClientPropertyVehicleDetailResponseDTO,
	ClientPropertyVehicleTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { useGetPropertyOptionalParams } from "web_myplann/Portfolio/Hooks/useGetPropertyOptionalParams";
import {
	DataCard,
	DataCardPreset,
} from "web_myplann/Shared/Components/DataCard";
import { DataNote } from "web_myplann/Shared/Components/DataNote";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import {
	dateFormatter,
	moneyFormatter,
	unitFormatter,
} from "web_myplann/Shared/Utils/formatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";

const PageResources = Resources.PortfolioDetail;

const securityTypes = [
	{ key: "carAlarmSecurity", label: PageResources.Property.CarAlarmSecurity },
	{
		key: "carActiveTrackingSecurity",
		label: PageResources.Property.CarActiveTrackingSecurity,
	},
	{
		key: "carPassiveTrackingSecurity",
		label: PageResources.Property.CarPassiveTrackingSecurity,
	},
	{
		key: "carMechanicalSecurity",
		label: PageResources.Property.CarMechanicalSecurity,
	},
	{
		key: "carImmobilizerSecurity",
		label: PageResources.Property.CarImmobilizerSecurity,
	},
	{
		key: "carGlassEtchingSecurity",
		label: PageResources.Property.CarGlassEtchingSecurity,
	},
];

const specialEquipment = [
	{
		key: "carSpecialEquipment44",
		label: PageResources.Property.CarSpecialEquipment44,
	},
	{
		key: "carSpecialEquipmentAudiovisualSystem",
		label: PageResources.Property.CarSpecialEquipmentAudiovisualSystem,
	},
	{
		key: "carSpecialEquipmentLeatherUpholstery",
		label: PageResources.Property.CarSpecialEquipmentLeatherUpholstery,
	},
	{
		key: "carSpecialEquipmentPanoramicRoof",
		label: PageResources.Property.CarSpecialEquipmentPanoramicRoof,
	},
	{
		key: "carSpecialEquipmentParkingAssistant",
		label: PageResources.Property.CarSpecialEquipmentParkingAssistant,
	},
];

type Props = {
	data: ClientPropertyVehicleDetailResponseDTO;
	currencySign: string;
	documentsId: number;
};

export const AutoMoto: FC<Props> = ({ data, currencySign, documentsId }) => {
	const { t } = useResource();
	const theme = useTheme();

	const { data: documents } = usePortfolioDocumentsQuery(documentsId);

	const { properties: carSecurity } = useGetPropertyOptionalParams(
		securityTypes,
		data,
		t,
	);
	const { properties: carSpecialEquipment } = useGetPropertyOptionalParams(
		specialEquipment,
		data,
		t,
	);

	const carType =
		data.propertyType &&
		data.propertyType !== ClientPropertyVehicleTypeEnum.None
			? t(PageResources.Property.CarPropertyTypeValue[data.propertyType])
			: EMPTY_DETAIL_FIELD;

	const carBodyType =
		data.carBodyType && data.carBodyType !== ClientCarBodyTypeEnum.None
			? t(PageResources.Property.CarBodyTypeValue[data.carBodyType])
			: EMPTY_DETAIL_FIELD;

	const carFuel =
		data.carFuel && data.carFuel !== ClientCarFuelEnum.None
			? t(PageResources.Property.CarFuelTypeValue[data.carFuel])
			: EMPTY_DETAIL_FIELD;

	const carVehicleType =
		data.carVehicleType &&
		data.carVehicleType !== ClientPropertyCarVehicleTypeEnum.None
			? t(PageResources.Property.CarVehicleTypeValue[data.carVehicleType])
			: EMPTY_DETAIL_FIELD;

	const carBrand =
		data.carBrand || data.carModel
			? `${data.carBrand ?? ""} ${data.carModel ?? ""}`
			: EMPTY_DETAIL_FIELD;

	const bottomComponent =
		data.carPurchaseDate ||
		data.carRegistration ||
		data.carManufactureMonthYear ||
		data.carPutIntoService ||
		data.description ? (
			<Stack>
				<DataRow
					label={t(PageResources.Property.CarPurchaseDate)}
					value={dateFormatter(data.carPurchaseDate)}
				/>
				<DataRow
					label={t(PageResources.Property.CarRegistration)}
					value={dateFormatter(data.carRegistration)}
				/>
				<DataRow
					label={t(PageResources.Property.CreatedAt)}
					value={data.carManufactureMonthYear}
				/>
				<DataRow
					label={t(PageResources.Property.CarPutIntoService)}
					value={dateFormatter(data.carPutIntoService)}
				/>
				<DataNote
					label={t(PageResources.Property.AdditionalDescription)}
					text={data.description}
				/>
			</Stack>
		) : null;

	return (
		<Stack>
			<DataCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={bottomComponent}
			>
				<Stack>
					<DataRow
						label={t(PageResources.Property.CarPropertyType)}
						value={carType}
					/>
					{data.propertyType &&
						data.propertyType ===
							ClientPropertyVehicleTypeEnum.OtherElectro && (
							<DataRow
								label={t(PageResources.Property.CarVehicleType)}
								value={carVehicleType}
							/>
						)}
					<DataRow
						label={t(PageResources.Property.CarLicensePlate)}
						value={data.carLicensePlate}
					/>
					<DataRow
						label={t(PageResources.Property.CarBrand)}
						value={carBrand}
					/>
					<DataRow
						label={t(PageResources.Property.CarVin)}
						value={data.carVin}
					/>
				</Stack>
			</DataCard>

			<DataCard
				label={t(PageResources.Property.CarValue)}
				icon={<Banknote size={16} color={theme.palette.deprecated.grey[700]} />}
			>
				<Stack>
					<DataRow
						label={t(PageResources.Property.Value)}
						value={moneyFormatter(data.value, currencySign)}
					/>
				</Stack>
			</DataCard>

			<DataCard
				label={t(PageResources.Property.VehicleParameters)}
				icon={<Box size={16} color={theme.palette.deprecated.grey[700]} />}
			>
				<Stack>
					<DataRow
						label={t(PageResources.Property.CarPower)}
						value={unitFormatter(data.carPower, "kW")}
					/>
					<DataRow
						label={t(PageResources.Property.CarVolume)}
						value={unitFormatter(data.carVolume, "ccm")}
					/>
					<DataRow
						label={t(PageResources.Property.CarBodyType)}
						value={carBodyType}
					/>
					<DataRow
						label={t(PageResources.Property.CarWeight)}
						value={unitFormatter(data.carWeight, "kg")}
					/>
					<DataRow
						label={t(PageResources.Property.CarSeatCount)}
						value={data.carSeatCount?.toLocaleString("cs-CZ")}
					/>
					<DataRow
						label={t(PageResources.Property.CarFuelType)}
						value={carFuel}
					/>
					<DataNote
						label={t(PageResources.Property.CarSecurity)}
						text={carSecurity}
					/>
					<DataNote
						label={t(PageResources.Property.CarSpecialEquipment)}
						text={carSpecialEquipment}
					/>
				</Stack>
			</DataCard>

			<RelatedSection
				documents={documents?.data}
				related={data.related}
				portfolioId={data.id}
			/>
		</Stack>
	);
};
